import { useWindowDimensions } from '../components/WebPrimitives';

/** 幅が狭い（小型スマートフォンの縦向き）とみなす最大の幅 */
const NARROW_MAX_WIDTH_PX = 420;
/** ヘッダーを圧縮する最大の高さ（高さの低い縦向き・横向き） */
const COMPACT_MAX_HEIGHT_PX = 760;
/** 横向きのスマートフォンとして、下部タブをさらに低くする最大の高さ */
const SHORT_MAX_HEIGHT_PX = 480;
/** 下部タブを横並びにできる最小の幅。横向きのスマートフォンは最小でも幅568px前後ある */
const SHORT_MIN_WIDTH_PX = 560;

/** 表示領域の大きさによる表示の段階 */
export type CompactViewport = Readonly<{
  /** 幅が狭いか。下部タブとカートの余白・字間を詰める */
  isNarrow: boolean;
  /** 幅が狭いか高さが低いか。ヘッダーの余白を詰める */
  isCompact: boolean;
  /**
   * 横向きのスマートフォンか（高さ480px以下・幅560px以上で横長）。下部タブを横並びにして本文の高さを残す。
   * 縦向きで高さだけが低い場合（画面分割・キーボード表示）は、高さが幅を下回っても横並びでは名前が入らないため含めない
   */
  isShort: boolean;
}>;

/**
 * 表示領域の幅と高さから表示の段階を決める
 * @param {number} width - 表示領域の幅
 * @param {number} height - 表示領域の高さ
 * @returns {CompactViewport} 表示の段階
 */
export const resolveCompactViewport = (width: number, height: number): CompactViewport => {
  /** 幅が狭いか */
  const isNarrow = width <= NARROW_MAX_WIDTH_PX;
  return {
    isNarrow,
    isCompact: isNarrow || height <= COMPACT_MAX_HEIGHT_PX,
    isShort: height <= SHORT_MAX_HEIGHT_PX && width >= SHORT_MIN_WIDTH_PX && width > height,
  };
};

/**
 * 画面共通の圧縮表示の判定を返す。ヘッダー・下部タブ・カートの基準は、ここの閾値だけで決める
 * @returns {CompactViewport} 表示の段階
 */
const useCompactViewport = (): CompactViewport => {
  const { width, height } = useWindowDimensions();
  return resolveCompactViewport(width, height);
};

export default useCompactViewport;
